import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import RightArrowCircleFilled from 'assets/icons/RightArrowCircleFilled.png'
import ConfirmationDialog from 'components/confirmation-dialog/confirmation-dialog.component'
import { RootState } from 'store'
import { TripPayload } from 'store/trips/trips.types'
import {
  removeOrderTasks,
  removeTaskById,
  removeTractor,
  removeTrailer,
  removeEnteredTrailerTask,
  selectOrderId,
  setOrderEventId,
} from 'store/trips/trips.slice'
import { useSessionOrderEventCleanup } from 'store/trips/use-session-order-event-cleanup'

import TripDetailsTableCell from '../../trip-details-table-cell/trip-details-table-cell'
import { useTripDetailsAction } from './trip-details-table-action.hooks'
import {
  isEnteredDropTrailerTask,
  isEnteredHookTrailerTask,
  isOrderLinkedHookTrailerTask,
} from '../../../trip-selection-old/trip-orders-list/trip-order-event.utils'

interface TripDetailsTableActionProps {
  tripTasks: TripPayload[]
  index: number
  onOpenDropAddress?: () => void
  hideLegacyIcon?: boolean
}

const TripDetailsTableAction = ({
  tripTasks,
  index,
  onOpenDropAddress,
  hideLegacyIcon = false,
}: TripDetailsTableActionProps) => {
  const [confirmPickupOpen, setConfirmPickupOpen] = useState(false)
  const [confirmDeliveryOpen, setConfirmDeliveryOpen] = useState(false)
  const { showCloseIcon } = useTripDetailsAction(tripTasks, index)
  const dispatch = useDispatch()
  const { deleteSessionOrderEvent } = useSessionOrderEventCleanup()
  const { newOrders, activerOrders } = useSelector(
    (state: RootState) => state.orders
  )

  const currentTask = tripTasks[index]

  const handleRemovePickupEvent = async () => {
    if (currentTask.orderEventId) {
      await deleteSessionOrderEvent(currentTask.orderEventId)
      dispatch(
        removeTaskById({
          orderEventId: currentTask.orderEventId,
          type: currentTask.type,
        })
      )
    }
    setConfirmPickupOpen(false)
  }

  const handleRemoveOrder = () => {
    if (currentTask.orderId) {
      dispatch(removeOrderTasks({ orderId: currentTask.orderId }))
    }
    setConfirmDeliveryOpen(false)
  }

  const handleDirectRemove = () => {
    switch (currentTask.type) {
      case 'HOOK_TRACTOR':
      case 'DROP_TRACTOR':
        dispatch(removeTractor())
        break
      case 'HOOK_TRAILER':
      case 'DROP_TRAILER':
        dispatch(removeTrailer())
        break
    }
  }

  const handleActionClick = () => {
    if (
      isEnteredDropTrailerTask(currentTask) ||
      isEnteredHookTrailerTask(currentTask)
    ) {
      dispatch(
        removeEnteredTrailerTask({
          type: currentTask.type,
          sourceOrderPkId: currentTask.sourceOrderPkId,
          orderId: currentTask.orderId,
        })
      )
      return
    }

    if (isOrderLinkedHookTrailerTask(currentTask)) {
      setConfirmPickupOpen(true)
      return
    }

    switch (currentTask.type) {
      case 'HOOK_TRACTOR':
      case 'DROP_TRACTOR':
      case 'HOOK_TRAILER':
      case 'DROP_TRAILER':
        handleDirectRemove()
        break
      case 'PICKUP':
        setConfirmPickupOpen(true)
        break
      case 'DELIVERY':
        setConfirmDeliveryOpen(true)
        break
    }
  }

  const handleDropAddress = () => {
    const allOrders = [...(newOrders ?? []), ...(activerOrders ?? [])]
    const order =
      allOrders.find((o) => o.userOrderId === currentTask.orderId) ??
      allOrders.find(
        (o) =>
          o.orderEvents?.some(
            (e) => e.orderEventId === currentTask.orderEventId
          ) ||
          o.exceptions?.some(
            (e) => e.orderEventId === currentTask.orderEventId
          )
      )

    if (order) {
      dispatch(selectOrderId({ orderId: order.orderId }))
    }
    dispatch(
      setOrderEventId({ orderEventId: currentTask.orderEventId ?? 0 })
    )
    setConfirmDeliveryOpen(false)
    setTimeout(() => onOpenDropAddress?.(), 0)
  }

  return (
    <>
      <TripDetailsTableCell minWidth={hideLegacyIcon ? 40 : 150}>
        {!hideLegacyIcon && showCloseIcon && (
          <img
            src={RightArrowCircleFilled}
            alt="action icon"
            width={27}
            height={27}
            style={{ cursor: 'pointer', transform: 'scaleX(-1)' }}
            onClick={handleActionClick}
          />
        )}
      </TripDetailsTableCell>

      <ConfirmationDialog
        title="Remove Order Event"
        open={confirmPickupOpen}
        handleClose={() => setConfirmPickupOpen(false)}
        successBtnText="Yes"
        cancelBtnText="No"
        handleSuccessBtn={handleRemovePickupEvent}
      >
        Are you sure you want to remove this order from the trip?
      </ConfirmationDialog>

      <ConfirmationDialog
        title="How would you like to proceed?"
        open={confirmDeliveryOpen}
        handleClose={() => setConfirmDeliveryOpen(false)}
        successBtnText="Remove all"
        cancelBtnText="Drop Address"
        handleSuccessBtn={handleRemoveOrder}
        handleCancelBtn={handleDropAddress}
      >
        {' '}
      </ConfirmationDialog>
    </>
  )
}

export default TripDetailsTableAction
